import { useEffect, useRef, useState } from 'react';

import { cyberMapRegistered, ensureCyberMap } from './vendor.js';
import { adopt } from '../../../vendor/maps/scenario.js';
import type { CyberLayers } from '../model/map-layers.js';
import type { ScenarioModel } from '../model/scenario.js';
import type { MapCommand } from '../state/store.js';
import type { MapContextKind, MapModifierClick } from './RegionMap.js';

/**
 * The typed React wrapper over `vendor/maps/cyber-map.js` (M5-D7) — the fourth,
 * and the closest to `maps/OrbitalMap.tsx` of the three that came before.
 *
 * Props in → `api` calls; `CustomEvent`s out → callbacks. The imperative
 * surface, from the renderer's own `boot()`:
 *
 * ```
 * select · focusUnit · focusRegion · focusRouter · setLayers · setDim · setPick
 * setCrossLink · setLink · setHover · fitSide · resetView · refresh
 * ```
 *
 * and out: `bs-map-ready`, `bs-select`, `bs-region`, `bs-pick`, `bs-context`,
 * `bs-hover`, `bs-mod-click`.
 *
 * ## It reaches past `api` nowhere, and it needs neither `refresh` nor `geometry`
 *
 * Phase 5's finding 3 (the theatre's two off-`api` calls) does not repeat, and
 * neither does the *shape* of Phase 6's answer, though the conclusion is the
 * same. `<orbital-map>` caches a merge pass keyed on the camera, so a snapshot
 * is pushed with `api.setLayers({})`. `<cyber-map>` caches **nothing at all**:
 * `render()` opens with `this.G = this.build()`, which walks `S.ROUTERS`,
 * `S.hostsOf` and `S.UNITS` from scratch on every frame. So any call that ends
 * in a draw pushes a new snapshot, and `setLayers({})` is used here for the same
 * reason it is there — it is on `api`, it is what the design's own bridge uses,
 * and it means this wrapper touches no private field.
 *
 * `api.refresh()` exists and is exactly `() => this.render()`. It is not called:
 * `setLayers({})` already renders, and two ways to say one thing is how the two
 * come to be used for different things by accident.
 *
 * ## `bs-region` carries a router, and the shell wants a network
 *
 * The third vocabulary through one event — a region id on the theatre map, a
 * band id on the orbital board, a **router** id here — and the first that is not
 * already the id the shell wants. `<cyber-map>` keys a segment on its router
 * machine and spends that one string four ways (uid, caption, `bs-select`,
 * `bs-region`), so what comes out of the title bar names the way in and what the
 * operator clicked on is the enclave. `model/cyber.ts`'s `networkOf` translates
 * it, here, once — the same shape `SdaView`'s `CONTEXT_SUBJECT` uses to turn
 * `region` into `band`, and for the same reason: the renderer reuses the event
 * and not the vocabulary.
 *
 * A router the wire would not name has the network id as its stand-in key, so
 * the translation is the identity for it and a click on that card selects the
 * enclave — which is the only thing there is to select.
 *
 * ## One key, and it is `Escape`
 *
 * The vendored element binds exactly one document key: `Escape`, guarded on
 * `isConnected`, clearing its own selection and segment. That is the same
 * overlap `region-map.js` and `orbital-map.js` already have (Phase 6's finding
 * 6) rather than a new one, and this board adds none of its own — no `v`, `r`,
 * `g`, `[` or `]`. The element is **removed** on unmount rather than hidden, so
 * even that one listener comes off `document` with it.
 */

/** The subset of the vendored element's surface this wrapper drives. */
interface CyberMapElement extends HTMLElement {
  api?: {
    select?: (id: string | null) => void;
    focusUnit?: (id: string) => void;
    focusRouter?: (id: string) => void;
    setLayers?: (layers: Partial<CyberLayers>) => void;
    setDim?: (ids: readonly string[] | null) => void;
    setPick?: (pick: { slot: string; ids: readonly string[] } | null) => void;
    setLink?: (link: { actorId: string; targetId: string; mission: string | null } | null) => void;
    setHover?: (id: string | null) => void;
    resetView?: () => void;
  };
}

/**
 * The tasking vector — the whole reason both networks share one canvas.
 *
 * `setLink` draws an arc from the actor to the target with the mission named on
 * it, and it is the one `api` call none of the other three renderers has. The
 * shell feeds it the **draft** in the builder, so an operator sees the order
 * they are composing before they queue it.
 */
export interface CyberLink {
  readonly actorId: string;
  readonly targetId: string;
  readonly mission: string | null;
}

export interface CyberMapProps {
  readonly model: ScenarioModel;
  readonly layers: CyberLayers;
  /** The selected asset or cyber contact. A selected *network* is not a unit. */
  readonly selectedUnit: string | null;
  /** Ids matching the roster filter, or `null` for no filter. Dimmed, never hidden. */
  readonly dim: readonly string[] | null;
  /** The open task slot's candidates — a click on one fills the slot. */
  readonly pick: { readonly slot: string; readonly ids: readonly string[] } | null;
  readonly hover: string | null;
  readonly link: CyberLink | null;
  readonly command: MapCommand | null;

  readonly onSelect: (id: string | null) => void;
  /** The **network**, already translated out of the router the event carried. */
  readonly onNetwork: (network: string | null) => void;
  readonly onPick: (slot: string, id: string) => void;
  readonly onContext: (kind: MapContextKind, id: string, x: number, y: number) => void;
  readonly onHover: (id: string | null) => void;
  readonly onModClick: (detail: MapModifierClick) => void;
  readonly onCommandApplied: (seq: number) => void;
}

const TAG = 'cyber-map';

export function CyberMap(props: CyberMapProps): JSX.Element {
  const hostRef = useRef<HTMLDivElement>(null);
  const elementRef = useRef<CyberMapElement | null>(null);
  const [registered, setRegistered] = useState(cyberMapRegistered);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let live = true;
    void ensureCyberMap().then(() => {
      if (live) setRegistered(cyberMapRegistered());
    });
    return () => {
      live = false;
    };
  }, []);

  /**
   * The last id the *element* reported selecting.
   *
   * Phase 5's guard, read carefully for a third canvas. It stops the console
   * fighting itself over a push, and it decides which `bs-region` to take —
   * `focusUnit` reports no region, but `clearSelection` and `selectSegment` both
   * emit `bs-select` **first**, so by the time the segment arrives this ref is
   * already `null` and the change is taken. Same ordering as the orbital board.
   */
  const echoed = useRef<string | null>(null);

  const handlers = useRef(props);
  handlers.current = props;

  const { model } = props;
  const initial = useRef({ model });

  useEffect(() => {
    const host = hostRef.current;
    if (host === null || !registered) return;

    // Seeded before the element is connected: `connectedCallback` reads the
    // adopted model in `boot()`.
    adopt(initial.current.model);

    const element = document.createElement(TAG) as CyberMapElement;
    element.style.width = '100%';
    element.style.height = '100%';
    element.setAttribute('data-testid', 'cyber-map-canvas');

    const onReady = (): void => setReady(true);
    const onSelect = (event: Event): void => {
      const id = (event as CustomEvent<string | null>).detail ?? null;
      echoed.current = id;
      handlers.current.onSelect(id);
    };
    const onRegion = (event: Event): void => {
      if (echoed.current !== null) return;
      const router = (event as CustomEvent<string | null>).detail ?? null;
      // The one translation this wrapper owns — see the header.
      handlers.current.onNetwork(router === null ? null : model.networkOf(router));
    };
    const onPick = (event: Event): void => {
      const detail = (event as CustomEvent<{ slot?: string; id?: string }>).detail;
      if (detail?.slot !== undefined && detail.id !== undefined) {
        handlers.current.onPick(detail.slot, detail.id);
      }
    };
    const onContext = (event: Event): void => {
      const detail = (
        event as CustomEvent<{
          kind: MapContextKind;
          id: string | null;
          x: number;
          y: number;
        }>
      ).detail;
      // `region` here is a segment title bar, and its id is the router. The
      // subject the shell opens is the *enclave*, so the id is translated on the
      // same seam the selection is — one place, not two.
      const id = detail.id ?? '';
      handlers.current.onContext(
        detail.kind,
        detail.kind === 'region' ? (model.networkOf(id) ?? id) : id,
        detail.x,
        detail.y,
      );
    };
    const onHover = (event: Event): void => {
      handlers.current.onHover((event as CustomEvent<string | null>).detail ?? null);
    };
    const onModClick = (event: Event): void => {
      handlers.current.onModClick((event as CustomEvent<MapModifierClick>).detail);
    };

    element.addEventListener('bs-map-ready', onReady);
    element.addEventListener('bs-select', onSelect);
    element.addEventListener('bs-region', onRegion);
    element.addEventListener('bs-pick', onPick);
    element.addEventListener('bs-context', onContext);
    element.addEventListener('bs-hover', onHover);
    element.addEventListener('bs-mod-click', onModClick);

    host.appendChild(element);
    elementRef.current = element;
    return () => {
      element.removeEventListener('bs-map-ready', onReady);
      element.removeEventListener('bs-select', onSelect);
      element.removeEventListener('bs-region', onRegion);
      element.removeEventListener('bs-pick', onPick);
      element.removeEventListener('bs-context', onContext);
      element.removeEventListener('bs-hover', onHover);
      element.removeEventListener('bs-mod-click', onModClick);
      // Removed rather than hidden: `disconnectedCallback` is what takes the
      // renderer's `Escape` listener off `document`.
      element.remove();
      elementRef.current = null;
      setReady(false);
    };
    // `model` is read inside for the router → network translation, and is
    // deliberately **not** a dependency: a new snapshot arrives on every
    // resolution and re-running this would tear the element down and throw away
    // the operator's camera. The effect below adopts the new model instead, and
    // the translation reads whatever `handlers.current` was closed over — which
    // is the same model, because `adopt` mutates the shared cell in place.
  }, [registered]);

  // A new model is a new snapshot. This renderer rebuilds its whole world on
  // every draw, so adopting plus any call that draws is the entire push.
  useEffect(() => {
    adopt(model);
    if (ready) elementRef.current?.api?.setLayers?.({});
  }, [model, ready]);

  const layers = props.layers;
  useEffect(() => {
    if (ready) elementRef.current?.api?.setLayers?.(layers);
  }, [layers, ready]);

  const selectedUnit = props.selectedUnit;
  useEffect(() => {
    if (!ready || selectedUnit === echoed.current) return;
    echoed.current = selectedUnit;
    elementRef.current?.api?.select?.(selectedUnit);
  }, [selectedUnit, ready]);

  const dim = props.dim;
  useEffect(() => {
    if (ready) elementRef.current?.api?.setDim?.(dim);
  }, [dim, ready]);

  const pick = props.pick;
  useEffect(() => {
    if (ready) elementRef.current?.api?.setPick?.(pick);
  }, [pick, ready]);

  const hover = props.hover;
  useEffect(() => {
    if (ready) elementRef.current?.api?.setHover?.(hover);
  }, [hover, ready]);

  const link = props.link;
  useEffect(() => {
    if (ready) elementRef.current?.api?.setLink?.(link);
  }, [link, ready]);

  const command = props.command;
  const onCommandApplied = props.onCommandApplied;
  useEffect(() => {
    if (!ready || command === null) return;
    const api = elementRef.current?.api;
    switch (command.kind) {
      case 'UNIT':
        api?.focusUnit?.(command.id);
        break;
      case 'NETWORK': {
        // Back the other way: the shell holds a network and the renderer focuses
        // a *router*, because that is what its segments are keyed on.
        const router = model.ROUTERS.find((candidate) => candidate.network === command.id);
        if (router !== undefined) api?.focusRouter?.(router.id);
        break;
      }
      case 'RESET':
        api?.resetView?.();
        break;
      // A ground zone and an orbital band are not on this canvas, and `fitSide`
      // is `() => {}` in the vendored file — so none of the three is offered
      // here, and none is silently swallowed somewhere else.
      case 'REGION':
      case 'BAND':
      case 'FIT':
        break;
    }
    onCommandApplied(command.seq);
  }, [command, ready, onCommandApplied, model]);

  return (
    <div
      ref={hostRef}
      data-testid="cyber-map"
      data-ready={ready ? 'true' : 'false'}
      style={{ flex: 1, minWidth: 0, minHeight: 0, position: 'relative' }}
    />
  );
}
